import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, CheckCircle2, Loader2, Store, XCircle } from "lucide-react";
import { toast } from "sonner";
import { businessTypeLabel } from "@/constants/verticals";

const PLAN_LABELS = {
  accounting: "نظام محاسبي فقط",
  ecommerce: "متجر إلكتروني فقط",
  accounting_ecommerce: "نظام محاسبي + متجر إلكتروني",
};
const STORE_PLANS = new Set(["ecommerce", "accounting_ecommerce"]);

export default function StoreApprovals() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/approvals");
      setCompanies((data || []).map(company => ({
        ...company,
        // Accept both names during deployment so an older API response cannot make
        // an already-open store look closed and turn the close action into another open.
        public_enabled: Boolean(company.public_enabled ?? company.public_store_enabled),
        subscription_plan: company.subscription_plan || (company.public_enabled || company.public_store_enabled ? "accounting_ecommerce" : "accounting"),
      })));
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (company) => {
    const nextEnabled = !company.public_enabled;
    if (nextEnabled && !STORE_PLANS.has(company.subscription_plan)) {
      toast.error("اختر اشتراك المتجر الإلكتروني أو الاشتراك المشترك من إعدادات الشركة أولًا");
      return;
    }
    setBusy(state => ({ ...state, [company.id]: true }));
    try {
      await api.put(`/store/approvals/${company.id}`, { public_enabled: nextEnabled });
      toast.success(nextEnabled ? "تم فتح المتجر للعامة" : "تم إغلاق المتجر وإخفاؤه من الدليل العام");
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(state => ({ ...state, [company.id]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="store-approvals-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary text-white grid place-items-center"><Store /></div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">فتح متاجر الشركات</h1>
          <p className="text-sm text-muted-foreground mt-1">يمكن للمدير العام الرئيسي فتح المتجر أو إغلاقه. الإغلاق يخفيه ويوقف الطلبات الجديدة من دون حذف بياناته.</p>
        </div>
      </div>
      {loading ? <Loader2 className="animate-spin mx-auto" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {companies.map(company => {
            const canEnable = STORE_PLANS.has(company.subscription_plan);
            return (
              <Card key={company.id} className="p-5">
                <div className="flex justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/10 text-primary grid place-items-center"><Building2 /></div>
                    <div className="min-w-0">
                      <h2 className="font-black truncate">{company.name}</h2>
                      <p className="text-sm text-primary mt-1">{businessTypeLabel(company.business_type) || company.business_type || "متجر"}</p>
                      <p className="text-xs text-muted-foreground mt-1">{PLAN_LABELS[company.subscription_plan] || PLAN_LABELS.accounting}</p>
                      <p className="text-xs text-muted-foreground mt-1">{company.address || "لا يوجد عنوان"}</p>
                    </div>
                  </div>
                  <Badge variant={company.public_enabled ? "default" : "secondary"}>{company.public_enabled ? "مفتوح للعامة" : "مغلق"}</Badge>
                </div>
                <Button
                  className="w-full mt-5"
                  variant={company.public_enabled ? "outline" : "default"}
                  onClick={() => toggle(company)}
                  disabled={!!busy[company.id] || (!company.public_enabled && !canEnable)}
                  data-testid={`store-toggle-${company.id}`}
                >
                  {busy[company.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : company.public_enabled
                    ? <><XCircle className="w-4 h-4 ml-2" />إغلاق المتجر</>
                    : <><CheckCircle2 className="w-4 h-4 ml-2" />فتح المتجر للعامة</>}
                </Button>
                {!company.public_enabled && !canEnable && <p className="text-xs text-amber-700 mt-2">لتفعيل المتجر، غيّر نوع الاشتراك إلى متجر إلكتروني أو اشتراك مشترك من إعدادات الشركة.</p>}
              </Card>
            );
          })}
          {!companies.length && <Card className="p-10 text-center text-muted-foreground md:col-span-2">لا توجد شركات.</Card>}
        </div>
      )}
    </div>
  );
}
